import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ArcadiaAppShell } from './ArcadiaAppShell';
import { TopPrimaryBar } from './TopPrimaryBar';
import { ConditionalSidebarWrapper } from './ConditionalSidebarWrapper';
import { useAuth } from '@/hooks/useAuth';
import { getLibraryEntriesWithGamesByUserId } from '@/lib/api/queries/library';
import { apiFetch } from '@/lib/api-fetch';
import type { Game, LibraryEntry } from '@/types';
import { AppLoadingSkeleton } from '@/components/layout/AppLoadingSkeleton';

async function isDeveloperForDisplayName(displayName: string): Promise<boolean> {
  const res = await apiFetch(
    `/api/catalog/developers/exists?name=${encodeURIComponent(displayName)}`
  );
  if (!res.ok) return false;
  const data = (await res.json()) as { exists?: boolean };
  return Boolean(data.exists);
}

export function ViteAppShell() {
  const { pathname } = useLocation();
  const { user, profile, loading } = useAuth();
  const [libraryEntries, setLibraryEntries] = useState<Array<{ entry: LibraryEntry; game: Game }>>([]);
  const [isDeveloper, setIsDeveloper] = useState(false);

  useEffect(() => {
    if (!user || !profile) {
      setLibraryEntries([]);
      setIsDeveloper(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const raw = await getLibraryEntriesWithGamesByUserId(user.id);
      const entries = raw
        .filter((e) => e.game !== null)
        .map((e) => ({
          entry: {
            id: e.id,
            userId: e.userId,
            gameId: e.gameId,
            status: e.status,
            pinned: e.pinned,
            totalPlaytimeHours: e.totalPlaytimeHours,
            lastPlayedAt: e.lastPlayedAt,
          },
          game: e.game!,
        }));
      const dev = await isDeveloperForDisplayName(profile.displayName);
      if (!cancelled) {
        setLibraryEntries(entries);
        setIsDeveloper(dev);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.displayName]);

  if (loading && pathname !== '/') {
    return <AppLoadingSkeleton />;
  }

  return (
    <ArcadiaAppShell
      profile={profile}
      topBar={<TopPrimaryBar profile={profile} />}
      sidebar={
        <ConditionalSidebarWrapper
          initialLibraryEntries={libraryEntries}
          profile={profile}
          isDeveloper={isDeveloper}
        />
      }
    >
      <Outlet />
    </ArcadiaAppShell>
  );
}
